import { RefObject, useRef, useState } from 'react'
import Cascader from 'antd/lib/cascader'
import classNames from 'classnames'
import './cascader-search.less'

export const CASCADER_SEARCHABLE_CLASS = 'per-cascader--searchable'
export const CASCADER_SEARCH_OPEN_CLASS = 'per-cascader--search-open'

type CascaderInputHandle = {
  input?: {
    input?: HTMLInputElement
  }
}

const getCascaderSearchInput = (cascader: CascaderInputHandle | null): HTMLInputElement | null =>
  cascader?.input?.input ??
  document.querySelector<HTMLInputElement>(`.${CASCADER_SEARCHABLE_CLASS}.${CASCADER_SEARCH_OPEN_CLASS} .ant-cascader-input`)

const prepareCascaderSearchInput = (cascader: CascaderInputHandle | null, placeholder: string): void => {
  const input = getCascaderSearchInput(cascader)
  if (!input) {
    return
  }

  input.value = ''
  input.placeholder = placeholder
}

/** Cascader skips placeholder when a value is set; our open-time DOM write then sticks across re-renders. */
const clearCascaderSearchInputPlaceholder = (cascader: CascaderInputHandle | null): void => {
  const input = getCascaderSearchInput(cascader)
  if (!input) {
    return
  }

  input.placeholder = ''
}

export type UseCascaderSearchOnOpenResult = {
  cascaderRef: RefObject<Cascader>
  cascaderClassName: string
  searchPlaceholder: string
  onPopupVisibleChange: (open: boolean) => void
}

/**
 * Ant Design 3 Cascader + showSearch: when a value is selected the picker label stays visible
 * and placeholder is omitted. This hook mirrors the dashboard Select search UX — on open, hide
 * the label and force the search placeholder; on close, clear the DOM placeholder.
 */
export const useCascaderSearchOnOpen = (placeholder: string): UseCascaderSearchOnOpenResult => {
  const cascaderRef = useRef<Cascader>(null)
  const [searchPopupOpen, setSearchPopupOpen] = useState(false)
  const searchPopupOpenRef = useRef(false)

  const onPopupVisibleChange = (open: boolean) => {
    const cascaderHandle = cascaderRef.current as unknown as CascaderInputHandle | null
    searchPopupOpenRef.current = open
    setSearchPopupOpen(open)

    if (open) {
      const prepare = () => {
        if (!searchPopupOpenRef.current) {
          return
        }
        prepareCascaderSearchInput(cascaderRef.current as unknown as CascaderInputHandle | null, placeholder)
      }
      requestAnimationFrame(() => {
        prepare()
        requestAnimationFrame(prepare)
      })
      return
    }

    clearCascaderSearchInputPlaceholder(cascaderHandle)
  }

  return {
    cascaderRef,
    cascaderClassName: classNames(CASCADER_SEARCHABLE_CLASS, {
      [CASCADER_SEARCH_OPEN_CLASS]: searchPopupOpen,
    }),
    searchPlaceholder: placeholder,
    onPopupVisibleChange,
  }
}
